import { Dialog, Portal, Image, CloseButton } from "@chakra-ui/react";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";

type Props = {
  img: string;
  localStorageKey: string;
  displayOncePer?: "day" | "week" | "month" | "lifetime";
  redirectUrl?: string;
  redirectOnBlank?: boolean;
  maxWidth?: string;
  maxHeight?: string;
};

const PopupModal = ({
  img,
  redirectUrl,
  redirectOnBlank,
  localStorageKey,
  maxWidth = "70vw",
  maxHeight = "70vh",
  displayOncePer,
}: Props): JSX.Element => {
  const [dateDisplayed, setDateDisplayed] = useLocalStorage<string | null>(
    localStorageKey,
    null
  );

  const shouldDisplay = useMemo(() => {
    switch (displayOncePer) {
      case "day":
        return dateDisplayed !== dayjs().format("YYYY-MM-DD");
      case "week":
        return (
          !dateDisplayed ||
          dateDisplayed < dayjs().subtract(1, "week").format("YYYY-MM-DD")
        );
      case "month":
        return (
          !dateDisplayed ||
          dateDisplayed < dayjs().subtract(1, "month").format("YYYY-MM-DD")
        );
      case "lifetime":
        return !dateDisplayed;
      default:
        return true;
    }
  }, [dateDisplayed, displayOncePer]);

  const handleClose = () => {
    setDateDisplayed(dayjs().format("YYYY-MM-DD"));
  };

  const handleClick = () => {
    if (redirectUrl)
      window.open(redirectUrl, redirectOnBlank ? "_blank" : "_self");
    handleClose();
  };

  if (!shouldDisplay) return <></>;

  return (
    <Dialog.Root
      open={true}
      onOpenChange={(e) => !e.open && handleClose()}
      size="lg"
      placement="center"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            position="relative"
            boxShadow="none"
            maxW="fit-content"
          >
            <CloseButton
              position="absolute"
              right={2}
              top={2}
              zIndex={1}
              color="gray.600"
              _hover={{ color: "gray.800" }}
              _focus={{ boxShadow: "none" }}
              onClick={handleClose}
              size="lg"
            />
            <Image
              src={img}
              alt="Popup Modal"
              onClick={handleClick}
              cursor={redirectUrl ? "pointer" : "default"}
              maxHeight={maxHeight}
              height="auto"
              width="auto"
              maxWidth={maxWidth}
              borderRadius="md"
            />
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
export default PopupModal;
